import { DataPicker, Typography } from '@/ui-kit/';
import styles from './date-registartion-form.module.scss';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { registrationStepDateSchema, RegistrationStepDateType } from '@/schemes/registration-step-date-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { throttle } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { type Profile } from '@/types/profile';
import { ButtonSubmitStep } from '@/components/form-registration-v2/components/button-submit-step';
import { format } from 'date-fns';
import { eventsApi } from '@/api';
import { callYaMetrika } from '@/utils/call-ya-metrika';
import { useToast } from '@/store/useToast';
import { SuccessNotificationIcon } from '@/assets/icons';
import { useTranslation } from 'react-i18next';

interface Props {
    userData?: Profile | null;
    onUpdateUserData: (data: RegistrationStepDateType) => Promise<any>;
    nextStep: () => void;
}

export const DateRegistartionForm: React.FC<Props> = ({ userData, onUpdateUserData, nextStep }) => {
    const showToast = useToast((state) => state.show);
    useEffect(() => {
        callYaMetrika('new_dob_screen');
        eventsApi.updateUserEvent('new_dob_screen');
    }, []);
    
    const initialValues = useMemo(() => {
        return {
            ...(
                userData?.dob ?
                    { dob: userData.dob}
                :
                    {}
            )
        };
    }, [userData]);

    const [isLoading, setIsLoading] = useState(false);

    const methods = useForm<RegistrationStepDateType>({
        resolver: zodResolver(registrationStepDateSchema),
        defaultValues: initialValues,
    });
    const {
        handleSubmit,
        formState: { errors, isDirty, isValid },
        watch,
    } = methods;

    const onSubmit = async (data: RegistrationStepDateType) => {
        try {
            setIsLoading(true);
            /*
             * for update only dirty data use code below
             * if (isDirty) {
             *    await onUpdateUserData(data);
             * }
             */
            await onUpdateUserData(data);
            /*
             * await get user data
             * force not call nextStep after update user data
             * nextStep();
             */
            nextStep();
            eventsApi.updateUserEvent('registration_step_complete_dob_screen');
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    const goalDobFieldChange = useCallback(
        throttle(() => {
            callYaMetrika('entered_in_DOB');
            eventsApi.updateUserEvent('registration_dob');
        }, 2000),
        [],
    );

    const dob = watch('dob');

    useEffect(() => {
        eventsApi.updateUserEvent('registration_step_view_dob_screen');
    }, [])

    const { t } = useTranslation();

    return (
        <FormProvider {...methods}>
            <form
                className={styles.form}
                onSubmit={handleSubmit(onSubmit)}>
                <div className={styles.formContainer}>
                    <Typography
                        variant="p"
                        fontWeight="medium"
                        className={styles.titleForm}
                        center>
                        {t('registration.when_is_your_birthday')}
                    </Typography>
                    <Typography
                        variant="p"
                        fontWeight="medium"
                        className={styles.subtitleForm}
                        center>
                        {t('registration.your_date_of_birth_will_only_be_used_in_questionnaire')}
                    </Typography>

                    <Controller
                        control={methods.control}
                        name={'dob'}
                        render={({ field: { onChange, value, ref } }) => {
                            return (
                                <DataPicker
                                    className={styles.datePicker}
                                    dob={methods.getValues('dob')}
                                    isRequireChanges={true}
                                    onChangeDate={(e) => {
                                        onChange(e)
                                        goalDobFieldChange();
                                    }} />
                            )}} />
                </div>
                <ButtonSubmitStep
                    isLoading={isLoading}
                    stepNumber={2}
                    isDisabled={!isValid} />
            </form>
        </FormProvider>
    );
};
